import moment from 'moment';
import { extractTimestamp } from '../../util/general';
import ApiService from '../../services/ApiService';
import { CardTransaction } from './types';

class CardPayment extends CardTransaction {
  constructor(tx: CardTransaction) {
    super();
    Object.assign(this, tx);
  }

  getName() {
    let firstName, lastName;
    if (this.cardName.includes('/')) {
      [lastName, firstName = ''] = this.cardName.split('/');
    } else {
      [firstName = '', lastName = ''] = this.cardName.split(' ');
    }

    return { lastName, firstName };
  }

  canVoid(): boolean {
    if (this.hasRefunds()) {
      return false;
    }

    // can only void on same day
    const paymentDate = extractTimestamp(this.id);
    return moment(paymentDate).format('YYYYMMDD') === moment().format('YYYYMMDD');
  }

  canRefund(): boolean {
    return this.isApproved() && !this.isVoid() && !this.isFullyRefunded();
  }

  doStatus() {
    return ApiService.post(`/payment/transaction/${this.id}/status`);
  }

  doRefund({
    amount,
    userInitials,
    sessionId,
    invoiceNum,
    deposit,
  }: {
    amount: number;
    userInitials: string;
    sessionId?: string;
    invoiceNum?: string;
    deposit?: boolean;
  }) {
    return ApiService.post(`/payment/transaction/${this.id}/refund`, {
      amount,
      userInitials,
      sessionId,
      invoiceNum,
      deposit,
    });
  }

  doVoid({ userInitials, sessionId = '' }: { userInitials: string; sessionId?: string }) {
    return ApiService.post(`/payment/transaction/${this.id}/void`, {
      amount: this.amount,
      userInitials,
      sessionId,
    });
  }

  async doVoidOrRefundPayment({ userInitials, sessionId = '' }: { userInitials: string; sessionId?: string }) {
    // make sure we only attempt to process an approved sale that isn't already refunded
    if (this.transactionType !== 'sale' || this.isVoid() || this.isFullyRefunded() || !this.isApproved()) {
      return;
    }

    if (this.canVoid()) {
      return this.doVoid({ userInitials, sessionId });
    } else {
      // attempt to refund remaining amount
      return this.doRefund({ amount: this.getNetAmount(), userInitials, sessionId });
    }
  }

  hasRefunds(): boolean {
    return !!this.refunds && !!this.refunds.length;
  }

  getRefundedAmount(): number {
    if (!this.refunds || !this.refunds.length) {
      return 0;
    }

    return parseFloat(
      this.refunds
        .map(p => p.amount)
        .reduce((a: number, b: number) => a + b, 0)
        .toFixed(2)
    );
  }

  getNetAmount() {
    return parseFloat((this.amount - this.getRefundedAmount()).toFixed(2));
  }

  isApproved(): boolean {
    return this.transactionResult === 'approved';
  }

  isFullyRefunded(): boolean {
    return this.hasRefunds() && this.getRefundedAmount() === this.amount;
  }

  isVoid(): boolean {
    return !!this.voidId;
  }
}

export default CardPayment;
